import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../atoms/Button/Button';
import { LinkIcon } from '../../../atoms/icons/LinkIcon';
import { useSlideOver } from '../../../legacy-components/slide-over/slide-over-context';
import { Tooltip } from '../../../legacy-components/tooltip';

export type DetailHeaderProps = {
  /**
   * `panel`: close button, title, then the actions, for a slide-over.
   * `page`: the breadcrumb with the actions on its row, for a full page.
   */
  variant: 'panel' | 'page';
  /** Panel only: the record's name. Long titles wrap rather than run under the actions. */
  title?: React.ReactNode;
  /** Panel only: closes the slide-over. Open full page closes it too. */
  onClose?: () => void;
  /** Page only: the page's breadcrumb. */
  breadcrumbs?: React.ReactNode;
  /**
   * The record's own buttons, e.g. Exempt. They come before the divider, which
   * shows only when one of them renders something. Mark a loading placeholder
   * with `data-placeholder` so it counts too.
   */
  actions?: React.ReactNode;
  /** A slot after `actions` that views can render their buttons into with a portal. */
  actionsRef?: React.Ref<HTMLDivElement>;
  /**
   * The record's full page, as a path or a URL. Shows Copy link, and in a panel
   * Open full page too. Open full page only shows for pages inside the app.
   */
  link?: string;
  /** Panel only: hides Open full page, e.g. where leaving the current page loses unsaved work. */
  hideOpenFullPage?: boolean;
};

// Rounded square with a grey icon; no outline, a light tint on hover.
const ICON_BUTTON_CLASSNAME =
  'rounded-lg! border-transparent! bg-transparent! text-gray-500! shadow-none! hover:bg-gray-100! hover:text-gray-900! dark:text-gray-400! dark:hover:bg-gray-700! dark:hover:text-gray-100! [&_svg]:h-4 [&_svg]:w-4';

// Tabler "arrows-diagonal" (MIT): opens the record as a full page. Not the
// four-corner fullscreen icon, and not an external-link arrow, which means
// leaving Plerion.
const ArrowsDiagonalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 4h4v4" />
    <path d="M14 10l6 -6" />
    <path d="M8 20h-4v-4" />
    <path d="M4 20l6 -6" />
  </svg>
);

const CopyLinkButton = ({ url }: { url: string }) => {
  const { t } = useTranslation('designSystem');
  const [isCopied, setIsCopied] = React.useState(false);

  React.useEffect(() => {
    if (!isCopied) {
      return;
    }
    const timeoutId = setTimeout(() => setIsCopied(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  return (
    <Tooltip label={isCopied ? t('linkCopied') : t('copyLink')}>
      <Button
        variant="secondary"
        size="small"
        icon={<LinkIcon className="h-3 w-6" />}
        aria-label={t('copyLink')}
        className={ICON_BUTTON_CLASSNAME}
        data-testid="copy-contextual-view-link-button"
        onClick={() =>
          navigator.clipboard.writeText(url).then(() => setIsCopied(true))
        }
      />
    </Tooltip>
  );
};

// Pages that would lose unsaved work if left (e.g. the workflow editor) hide
// Open full page in every panel while they are on screen. Panels render at the
// app root, outside those pages, so this lives outside React context.
let openFullPageBlockers = 0;
const blockerListeners = new Set<() => void>();
const subscribeToBlockers = (listener: () => void) => {
  blockerListeners.add(listener);
  return () => {
    blockerListeners.delete(listener);
  };
};
const isOpenFullPageBlocked = () => openFullPageBlockers > 0;

/** Hides Open full page in every panel while the calling component is mounted. */
export const useHideOpenFullPageWhileMounted = () => {
  React.useEffect(() => {
    openFullPageBlockers += 1;
    blockerListeners.forEach((listener) => listener());
    return () => {
      openFullPageBlockers -= 1;
      blockerListeners.forEach((listener) => listener());
    };
  }, []);
};

/** The app path of a link, or undefined when it points outside the app. */
const toAppPath = (link: string) => {
  if (link.startsWith('/')) {
    return link;
  }
  try {
    const url = new URL(link);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The header of a record's slide-over and of its full page, so both look the
 * same everywhere: close button and title (panel) or breadcrumb (page), then the
 * record's actions, a divider, Copy link and Open full page.
 *
 * Example:
 * ```tsx
 *   <DetailHeader variant="panel" title="Asset details" onClose={close}
 *     actions={<ExemptButton />} link="/assets/abc" />
 * ```
 */
export const DetailHeader = ({
  variant,
  title,
  onClose,
  breadcrumbs,
  actions,
  actionsRef,
  link,
  hideOpenFullPage = false,
}: DetailHeaderProps) => {
  const { t } = useTranslation('designSystem');
  const navigate = useNavigate();
  const { closeAllSlideOvers } = useSlideOver();
  const isBlocked = React.useSyncExternalStore(
    subscribeToBlockers,
    isOpenFullPageBlocked,
  );
  const appPath = link ? toAppPath(link) : undefined;
  const isPanel = variant === 'panel';

  const actionRow = (
    <div className="flex shrink-0 items-center gap-2">
      {/* Counts as having actions only when a child has content or is a marked
          loading placeholder: the portal slot and buttons that render nothing
          don't. The divider shows only then. */}
      <div className="peer flex items-center gap-2 [&:not(:has(>:not(:empty),>[data-placeholder]))]:hidden">
        {actions}
        {actionsRef && (
          <div
            ref={actionsRef}
            className="flex items-center gap-2 empty:hidden"
          />
        )}
      </div>
      {link && (
        <>
          <span
            aria-hidden="true"
            className="mx-1 hidden h-5 w-px bg-gray-300 peer-has-[>:not(:empty),>[data-placeholder]]:block dark:bg-gray-600"
          />
          <CopyLinkButton
            url={appPath ? `${window.location.origin}${appPath}` : link}
          />
          {isPanel && appPath && !hideOpenFullPage && !isBlocked && (
            <Tooltip label={t('openFullPage')}>
              <Button
                variant="secondary"
                size="small"
                icon={<ArrowsDiagonalIcon />}
                aria-label={t('openFullPage')}
                className={ICON_BUTTON_CLASSNAME}
                data-testid="open-full-page-button"
                onClick={() => {
                  // Every panel, not just this one: a panel opened from another
                  // panel would otherwise leave the one below over the new page.
                  onClose?.();
                  closeAllSlideOvers?.();
                  navigate(appPath);
                }}
              />
            </Tooltip>
          )}
        </>
      )}
    </div>
  );

  if (!isPanel) {
    // No padding of its own: the page around it sets the spacing.
    return (
      <div className="flex min-w-0 items-center justify-between gap-4">
        {/* Shrinks, so a long last crumb gets "…" before the actions. */}
        <div className="min-w-0 flex-1">{breadcrumbs}</div>
        {actionRow}
      </div>
    );
  }

  return (
    // Same background as the cards in the panel body; sides line up with them.
    <div className="border-dark-gray px-xl py-15px flex min-w-0 items-center gap-3 border-b bg-white dark:bg-gray-800">
      {onClose && (
        <button
          type="button"
          aria-label={t('closePanel')}
          className="focus-visible:ring-plerion-burnt-violet-alternate inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-100 dark:focus-visible:ring-white dark:focus-visible:ring-offset-gray-800"
          data-testid="close-contextual-view"
          onClick={onClose}
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      {/* Takes the space left, so a long title wraps (up to two lines, then "…",
          full on hover) rather than running under the actions. */}
      <div className="min-w-0 flex-1">
        {/* Kept an h1: e2e tests find some slide-overs by this heading. */}
        {title && (
          <h1
            title={typeof title === 'string' ? title : undefined}
            className="text-darkest-gray line-clamp-2 text-lg break-words"
          >
            {title}
          </h1>
        )}
      </div>
      {actionRow}
    </div>
  );
};
